import { useMemo } from 'react';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Setting } from 'src/orchd-client';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { Alert } from 'src/components/Alert';
import { Box } from 'src/components/Box';
import { Divider } from 'src/components/Divider';
import { TitleBlurbBodyLayout } from 'src/components/Layout/TitleBlurbBody';
import { LoadingOverlay } from 'src/components/LoadingOverlay/LoadingOverlay';
import { SelectField } from 'src/components/Select';
import { Toggle } from 'src/components/Toggle';
import { useMountEffect } from 'src/hooks/useMountEffect/useMountEffect';
import { messages } from 'src/messages';
import { AppState } from 'src/store';
import { promisifyRequestAction } from 'src/store/helpers';
import { settingsActions } from 'src/store/settings/actions';
import { settingsSelectors } from 'src/store/settings/selectors';
import { toastActions } from 'src/store/toast/actions';
import { getErrorInfo } from 'src/utils/errors';

type ActionProps = typeof dispatchToProps;

interface Props extends ActionProps {
  allSettings?: Setting[];
  isAllSettingsLoading: boolean;
}

export const SERVER_SELECTION_KEY = 'server_selection_criteria';
export const SERVER_CUSTOMER_STICKINESS_KEY = 'org_websites_same_server';
export const SERVER_SUBSCRIPTION_STICKINESS_KEY = 'subscription_websites_same_server';
export const STICKY_EMAIL_PLACEMENT_KEY = 'sticky_email_placement';

export const _ServerPlacement = ({
  allSettings = [],
  isAllSettingsLoading,
  updateSettings,
  getSettings,
  successToast,
  errorToast,
}: Props) => {
  const { formatMessage } = useIntl();

  useMountEffect(() => {
    getSettings({});
  });

  const random = useMemo(
    () => ({
      value: 'random',
      label: formatMessage({
        id: 'platform_settings.server_placement.server_selection.random',
        defaultMessage: 'Random',
      }),
    }),
    [formatMessage]
  );

  const leastLoad = useMemo(
    () => ({
      value: 'leastLoad',
      label: formatMessage({
        id: 'platform_settings.server_placement.server_selection.least_load',
        defaultMessage: 'Least load',
      }),
    }),
    [formatMessage]
  );

  const leastWebsites = useMemo(
    () => ({
      value: 'leastWebsiteCount',
      label: formatMessage({
        id: 'platform_settings.server_placement.server_selection.least_websites',
        defaultMessage: 'Least websites',
      }),
    }),
    [formatMessage]
  );

  const updateServerSelection = async (newValue: string) => {
    try {
      await updateSettings({
        name: SERVER_SELECTION_KEY,
        stringNumberBooleanArrayanyObject: newValue,
      });

      successToast(
        formatMessage({
          id: 'platform_settings.server_placement.server_selection.updated_success',
          defaultMessage: 'Server selection setting updated',
        })
      );
    } catch {
      errorToast(
        formatMessage({
          id: 'platform_settings.server_placement.server_selection.updated_error',
          defaultMessage: 'Error updating server selection setting',
        })
      );
    }
    getSettings({ params: {} });
  };

  const serverSelectionValue = useMemo(() => {
    const criteria = allSettings.find((value) => value.name === SERVER_SELECTION_KEY);

    if (criteria?.value === 'leastLoad') {
      return leastLoad;
    }

    if (criteria?.value === 'leastWebsiteCount') {
      return leastWebsites;
    }

    return random;
  }, [allSettings, leastLoad, leastWebsites, random]);

  const serverCustomerStickinessValue = useMemo(() => {
    const customerStickiness = allSettings.find((value) => value.name === SERVER_CUSTOMER_STICKINESS_KEY);

    return customerStickiness?.value === true;
  }, [allSettings]);

  const serverSubscriptionStickinessValue = useMemo(() => {
    const subscriptionStickiness = allSettings.find((value) => value.name === SERVER_SUBSCRIPTION_STICKINESS_KEY);

    return subscriptionStickiness?.value === true;
  }, [allSettings]);

  const stickyEmailPlacementvalue = useMemo(() => {
    const subscriptionStickiness = allSettings.find((value) => value.name === STICKY_EMAIL_PLACEMENT_KEY);

    return subscriptionStickiness?.value === true;
  }, [allSettings]);

  const updateServerCustomerStickiness = async (stickiness: boolean) => {
    try {
      await updateSettings({
        name: SERVER_CUSTOMER_STICKINESS_KEY,
        stringNumberBooleanArrayanyObject: stickiness,
      });

      const message = stickiness ? messages.item_enabled : messages.item_disabled;
      successToast(formatMessage(message, { item: formatMessage(messages.customer_stickiness) }));
    } catch (e) {
      const { message: error } = getErrorInfo(e);

      const message = stickiness ? messages.item_enabled_error_detail : messages.item_disabled_error_detail;
      errorToast(formatMessage(message, { item: formatMessage(messages.customer_stickiness), error }));
    }

    getSettings({});
  };

  const updateServerSubscriptionStickiness = async (stickiness: boolean) => {
    try {
      await updateSettings({
        name: SERVER_SUBSCRIPTION_STICKINESS_KEY,
        stringNumberBooleanArrayanyObject: stickiness,
      });

      if (stickiness && !serverCustomerStickinessValue) {
        await updateSettings({
          name: SERVER_CUSTOMER_STICKINESS_KEY,
          stringNumberBooleanArrayanyObject: stickiness,
        });
      }

      const message = stickiness ? messages.item_enabled : messages.item_disabled;
      successToast(formatMessage(message, { item: formatMessage(messages.subscription_stickiness) }));
    } catch (e) {
      const { message: error } = getErrorInfo(e);
      const message = stickiness ? messages.item_enabled_error_detail : messages.item_disabled_error_detail;
      errorToast(formatMessage(message, { item: formatMessage(messages.subscription_stickiness), error }));
    }

    getSettings({});
  };

  const updateStickyEmailPlacement = async (stickiness: boolean) => {
    try {
      await updateSettings({
        name: STICKY_EMAIL_PLACEMENT_KEY,
        stringNumberBooleanArrayanyObject: stickiness,
      });

      const message = stickiness ? messages.item_enabled : messages.item_disabled;
      successToast(formatMessage(message, { item: formatMessage(messages.subscription_stickiness) }));
    } catch (e) {
      const { message: error } = getErrorInfo(e);
      const message = stickiness ? messages.item_enabled_error_detail : messages.item_disabled_error_detail;
      errorToast(formatMessage(message, { item: formatMessage(messages.subscription_stickiness), error }));
    }

    getSettings({});
  };

  return (
    <Box dataDemoDisabled>
      {isAllSettingsLoading && <LoadingOverlay />}

      <Alert
        variant="info"
        title={
          <FormattedMessage
            id="platform_settings.server_placement.note.title"
            defaultMessage="'Customer stickiness' and 'Subscription stickiness' settings take precedence over 'Server selection method'."
          />
        }
        marginBottom="xl"
      >
        <FormattedMessage
          id="platform_settings.server_placement.note.body"
          defaultMessage="Any 'Server placement' overrides (made while adding a website) and dedicated servers take precedence over these settings."
        />
      </Alert>

      <TitleBlurbBodyLayout
        title={formatMessage({
          id: 'platform_settings.server_placement.server_selection',
          defaultMessage: 'Server selection method',
        })}
        blurb={formatMessage({
          id: 'platform_settings.server_placement.server_selection.help',
          defaultMessage: 'New websites added to your cluster will be assigned to servers based on this setting.',
        })}
        marginBottom="2xl"
      >
        <SelectField
          options={[leastLoad, leastWebsites, random]}
          name="selectionMethod"
          value={serverSelectionValue}
          onChange={({ value }) => {
            updateServerSelection(value[0].value.toString());
          }}
        />
      </TitleBlurbBodyLayout>

      <Divider marginBottom="2xl" />

      <TitleBlurbBodyLayout
        title={formatMessage(messages.customer_stickiness)}
        blurb={formatMessage({
          id: 'platform_settings.server_placement.customer_stickiness.help',
          defaultMessage:
            'Place all websites owned by a single customer on the same server. If the existing server is unavailable, websites will be placed on alternative severs.',
        })}
        marginBottom="2xl"
      >
        <Toggle
          name="customerStickiness"
          checked={serverCustomerStickinessValue}
          onChange={({ checked }) => updateServerCustomerStickiness(checked)}
        />
      </TitleBlurbBodyLayout>

      <Divider marginBottom="2xl" />

      <TitleBlurbBodyLayout
        title={formatMessage(messages.subscription_stickiness)}
        blurb={formatMessage({
          id: 'platform_settings.server_placement.subscription_stickiness.help',
          defaultMessage: 'Attempt to place all websites under a subscription onto the same server.',
        })}
        marginBottom="2xl"
      >
        <Toggle
          name="subscriptionStickiness"
          checked={serverSubscriptionStickinessValue}
          onChange={({ checked }) => updateServerSubscriptionStickiness(checked)}
        />
      </TitleBlurbBodyLayout>

      <Divider marginBottom="2xl" />

      <TitleBlurbBodyLayout
        title={formatMessage(messages.email_stickiness)}
        blurb={formatMessage({
          id: 'platform_settings.server_placement.email_stickiness.help',
          defaultMessage: 'Place email role on the same role as the application role if the email role is installed.',
        })}
        marginBottom="2xl"
      >
        <Toggle
          name="subscriptionStickiness"
          checked={stickyEmailPlacementvalue}
          onChange={({ checked }) => updateStickyEmailPlacement(checked)}
        />
      </TitleBlurbBodyLayout>

      {/* TODO: Waiting for backend support. */}
      {/* <Divider marginBottom="2xl" />

      <TitleBlurbBodyLayout
        title={formatMessage({
          id: 'platform_settings.server_placement.website_stickiness',
          defaultMessage: 'Website stickiness',
        })}
        blurb={formatMessage({
          id: 'platform_settings.server_placement.website_stickiness.help',
          defaultMessage: 'Attempt to place all roles for a website on the same server',
        })}
      >
        <Toggle name="websiteStickiness" checked={false} onChange={() => {}} />
      </TitleBlurbBodyLayout> */}
    </Box>
  );
};

const stateToProps = (state: AppState) => ({
  allSettings: settingsSelectors.settingsState(state).data?.items,
  isAllSettingsLoading: settingsSelectors.settingsState(state).loading,
});

export const dispatchToProps = {
  updateSettings: promisifyRequestAction(settingsActions.updateSetting.request),
  getSettings: promisifyRequestAction(settingsActions.getSettings.request),
  successToast: toastActions.successToast,
  errorToast: toastActions.errorToast,
};

export const ServerPlacement = connect(stateToProps, dispatchToProps)(_ServerPlacement);
